import { SlotProps, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import DropdownArrow from '@/assets/icons/dropdown-arrow.svg?react';
import { CustomSelectItem } from '@/components/molecules/CustomSelectItem';
import { useResizeObserver } from '@/hooks/useResizeObserver';
import { useTranslator } from '@/hooks/useTranslator';
import { SelectItemWithDescription } from '@/types/fields';
import { computeStyles } from '@/utility/customSelectComputeStyles';

interface Props {
  readonly value?: string;
  readonly label: string;
  readonly items: readonly SelectItemWithDescription[];
  readonly error?: boolean;
  readonly helperText?: string;
  readonly fullWidth?: boolean;

  onBlur?(event: React.FocusEvent<HTMLInputElement>): void;
  onChange?(event: React.ChangeEvent<HTMLInputElement>): void;
}

export const CustomSelect: React.FC<Props> = (props: Props): React.ReactElement => {
  const { value, items, label, fullWidth, error, helperText, onChange } = props;

  const [button, setButton] = React.useState<HTMLInputElement | null>(null);
  const [expanded, setExpanded] = React.useState<boolean>(false);
  const [containerBox, setContainerBox] = React.useState<DOMRect>(new DOMRect(0, 0, 0, 0));
  const [container, setContainer] = React.useState<HTMLUListElement | null>(null);
  const {
    palette: { text },
  } = useTheme();

  const boxWidth = React.useMemo(
    (): '100%' | undefined => (fullWidth ? '100%' : undefined),
    [fullWidth],
  );

  const updateContainerBox = React.useCallback((entries: readonly ResizeObserverEntry[]): void => {
    const { target } = entries[0];
    setContainerBox(target.getBoundingClientRect());
  }, []);
  useResizeObserver(container, updateContainerBox);

  const tr = useTranslator();
  const sx = React.useMemo(
    (): readonly SystemStyleObject[] => computeStyles(containerBox, container),
    [container, containerBox],
  );

  const collapse = React.useCallback((): void => {
    setExpanded(false);
  }, []);

  const toggleExpand = React.useCallback((event: React.MouseEvent): void => {
    event.stopPropagation();
    setExpanded(true);
  }, []);

  const handleItemClick = React.useCallback(
    (selectedValue: string): void => {
      const event = new Event('change', {
        bubbles: true,
      }) as unknown as React.ChangeEvent<HTMLInputElement>;
      if (!button) {
        console.debug('cannot send the change event');
        return;
      }
      button.value = selectedValue;

      Object.defineProperty(event, 'target', { value: button, writable: true });
      Object.defineProperty(event, 'currentTarget', { value: button, writable: true });

      onChange?.(event);
      button?.focus();
    },
    [button, onChange],
  );

  React.useEffect((): void => {
    if (expanded && container) {
      requestAnimationFrame((): void => {
        setContainerBox(container.getBoundingClientRect());
      });
    }
  }, [container, expanded]);

  const slotProps = React.useMemo((): SlotProps<any, any, any> => {
    const style: readonly SystemStyleObject[] = [
      {
        width: 24,
        height: 24,
      },
      {
        transform: expanded ? 'rotate(180deg)' : undefined,
      },
    ];

    return {
      htmlInput: {
        tabIndex: -1,
        pointerEvents: 'none',
      },
      inputAdornment: {
        color: 'white',
      },
      input: {
        pointerEvents: 'none',
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end" sx={style}>
            <DropdownArrow fill={text.primary} />
          </InputAdornment>
        ),
      },
    };
  }, [expanded, text.primary]);

  const displayValue = React.useMemo((): string => {
    return tr(
      items.find((item: SelectItemWithDescription): boolean => item.value === value)?.label ?? '',
    );
  }, [items, value, tr]);

  return (
    <Box sx={styles.container} width={boxWidth} ref={setContainer}>
      <TextField
        sx={styles.input}
        value={displayValue}
        slotProps={slotProps}
        label={label}
        variant="outlined"
        fullWidth={true}
        error={error}
        helperText={helperText}
      />

      <Box
        ref={setButton}
        position="absolute"
        component="button"
        sx={styles.overlay}
        top={0}
        left={0}
        width="100%"
        height="100%"
        borderRadius={6}
        onClick={toggleExpand}
      />

      {expanded ? (
        <Box sx={styles.backdrop} onClick={collapse}>
          <Box component="ul" sx={sx}>
            <Box sx={styles.paperContainer}>
              <Paper sx={styles.paper} elevation={1}>
                {items.map((item: SelectItemWithDescription): React.ReactNode => {
                  return (
                    <CustomSelectItem key={item.value} item={item} onClick={handleItemClick} />
                  );
                })}
              </Paper>
            </Box>
          </Box>
        </Box>
      ) : (
        <></>
      )}
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    position: 'relative',
  },
  backdrop: {
    position: 'fixed',
    inset: 8,
    zIndex: 3,
  },
  paper: {
    width: '100%',
    height: '100%',
    backgroundColor: 'custom.White',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  paperContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    borderRadius: 4,
  },
  overlay: {
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    borderColor: 'custom.DividerLight',
    borderWidth: 1,
    borderStyle: 'solid',
    '&:focus': {
      borderColor: 'text.primary',
    },
    '&:hover': {
      borderColor: 'text.primary',
    },
  },
  input: {
    fieldset: {
      borderColor: 'transparent',
    },
    '& .MuiAdornment-root': {
      color: 'white',
    },
  },
};
